<script setup lang="ts">
import { trans } from '@tools/translation.ts';
import { ref, type ComponentPublicInstance, computed, watch, onMounted } from 'vue';
import AutoComplete, { type AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import { PlaceType } from '@global-types/googleAddress';
import {
  type ExtendedGoogleAddressSuggestion,
  getAddresses,
  getAddressByCoordinates,
  type GoogleAutocompleteParams,
} from '@api/google/googlePlaces';
import Typography from '@components/Typography.vue';
import Icon from '@components/Icon.vue';
import Button from '@components/Button.vue';
import Spinner from '@components/Spinner.vue';
import Divider from '@components/Divider.vue';
import {
  IconMagnifyingGlass,
  IconX,
  IconCrosshairFill,
  IconClockClockwise,
  IconMapPinMagic,
  IconArrowRight,
} from '@icons';
import { flashBagError } from '@tools/flashbag.ts';
import { useLoading } from '@composables/useLoading.ts';
import { shortenStreet } from '@tools/address-utils.ts';

const suggestedLocations = ref<ExtendedGoogleAddressSuggestion[]>([]);
const selectedLocations = ref<ExtendedGoogleAddressSuggestion[]>([]);
const lastSelectedLocations = ref<ExtendedGoogleAddressSuggestion[] | null>(null);

const autocompleteBuffer = ref<ExtendedGoogleAddressSuggestion | string | null>(null);
const autocompleteRef = ref<ComponentPublicInstance | null>(null);

const highlightedIndex = ref<number>(0);

const isGeolocated = ref<boolean>(false);

const getOptionLabel = (option: ExtendedGoogleAddressSuggestion | string | null): string => {
  if (!option) {
    return '';
  }

  if (typeof option === 'string') {
    return option;
  }

  return shortenStreet(option.mainText);
};

const formatLocationTitle = (location: ExtendedGoogleAddressSuggestion): string => {
  const city = location.secondaryText.replace(', France', '').trim();

  if (location.type === PlaceType.POSTAL_CODE) {
    return city;
  }

  if (location.type === PlaceType.DISTRICT || location.type === PlaceType.DISTRICT_LEVEL_1) {
    const district = location.mainText
      .replace(/(è?me|e|er)\s*Arrondissement/i, '')
      .trim();

    if (district.toLowerCase().startsWith(city.toLowerCase())) {
      return district;
    }

    return `${city} ${district}`.trim();
  }

  return shortenStreet(location.mainText);
};

const multiSelectablePlaceTypes: PlaceType[] = [
  PlaceType.CITY,
  PlaceType.POSTAL_CODE,
  PlaceType.DISTRICT,
  PlaceType.DISTRICT_LEVEL_1,
] as const;

const selectedLocationType = computed<PlaceType | null>(
  () => (selectedLocations.value.length ? selectedLocations.value[0].type : null),
);

const allowMultipleSelection = computed<boolean>(() => {
  if (!selectedLocationType.value) {
    return false;
  }

  return multiSelectablePlaceTypes.includes(selectedLocationType.value);
});

const lastSelectedLocationsText = computed<string>(
  () => {
    if (!lastSelectedLocations.value?.length) {
      return '';
    }

    const firstLocation = lastSelectedLocations.value[0];
    const isMultiSelectable = !!firstLocation.type && multiSelectablePlaceTypes.includes(firstLocation.type);

    if (lastSelectedLocations.value.length === 1 && !isMultiSelectable) {
      return `${shortenStreet(firstLocation.mainText)}, ${firstLocation.secondaryText}`;
    }

    return lastSelectedLocations.value.map((location) => formatLocationTitle(location)).join(', ');
  },
);

const searchParams = computed<GoogleAutocompleteParams | undefined>(() => {
  if (!allowMultipleSelection.value || !selectedLocations.value.length) {
    return undefined;
  }

  return {
    placeTypes: multiSelectablePlaceTypes,
  };
});

const {
  isLoading: isGeolocating,
  startLoading: startGeolocating,
  stopLoading: stopGeolocating,
} = useLoading();

const focusInput = (): void => {
  autocompleteRef.value?.$el?.querySelector('input')?.focus();
};

const search = async (event: AutoCompleteCompleteEvent): Promise<void> => {
  const { query } = event;
  if (!query) {
    suggestedLocations.value = [];
    return;
  }

  suggestedLocations.value = await getAddresses(query, searchParams.value);
};

const clearInput = (): void => {
  suggestedLocations.value = [];
  autocompleteBuffer.value = null;
  focusInput();
};

const clear = (): void => {
  clearInput();
  selectedLocations.value = [];
};

const selectSuggestion = (location: ExtendedGoogleAddressSuggestion, forceUnique: boolean = false): void => {
  if (forceUnique) {
    selectedLocations.value = [ location ];
    lastSelectedLocations.value = [ ...selectedLocations.value ];

    suggestedLocations.value = [];
    autocompleteBuffer.value = location;
    focusInput();

    return;
  }

  const isAlreadySelected = selectedLocations.value.some(
    (selectedLocation) => selectedLocation.placeId === location.placeId
      || formatLocationTitle(selectedLocation) === formatLocationTitle(location),
  );

  if (isAlreadySelected) {
    clearInput();
    return;
  }

  selectedLocations.value = allowMultipleSelection.value
    ? [ ...selectedLocations.value, location ]
    : [ location ];

  lastSelectedLocations.value = [ ...selectedLocations.value ];

  if (allowMultipleSelection.value) {
    clearInput();
    return;
  }

  suggestedLocations.value = [];
  autocompleteBuffer.value = location;
  focusInput();
};

const onKeyDown = (event: KeyboardEvent): void => {
  if (!suggestedLocations.value.length) {
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    highlightedIndex.value = (highlightedIndex.value + 1) % suggestedLocations.value.length;
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    highlightedIndex.value = (highlightedIndex.value - 1 + suggestedLocations.value.length) % suggestedLocations.value.length;
    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    selectSuggestion(suggestedLocations.value[highlightedIndex.value]);
  }
};

const removeSuggestion = (location: ExtendedGoogleAddressSuggestion): void => {
  const index = selectedLocations.value.findIndex((selectedLocation) => selectedLocation.placeId === location.placeId);
  if (index === -1) {
    return;
  }

  selectedLocations.value.splice(index, 1);
};

const getTypeLabel = (type: PlaceType | null): string | null => {
  if (!type) {
    return trans('search.filters.location.type.address');
  }

  const label = trans(`search.filters.location.type.${type}`);

  if (!label.includes('search.filters')) {
    return label;
  }

  return trans('search.filters.location.type.address');
};

const getCurrentGeolocationPosition = (): Promise<GeolocationPosition> => new Promise((resolve, reject) => {
  navigator.geolocation.getCurrentPosition(resolve, reject);
});

const selfGeolocate = async (): Promise<void> => {
  if (isGeolocating.value) {
    return;
  }

  startGeolocating();

  try {
    const position = await getCurrentGeolocationPosition();

    const { latitude, longitude } = position.coords;
    const location = await getAddressByCoordinates(latitude, longitude);

    if (!location) {
      flashBagError();
      stopGeolocating();
      return;
    }

    selectSuggestion(location, true);

    autocompleteBuffer.value = location;

    isGeolocated.value = true;
  } catch (error: unknown) {
    if (
      error instanceof GeolocationPositionError
      && error.code !== error.PERMISSION_DENIED
    ) {
      flashBagError();
    }
  }

  stopGeolocating();
};

const selectLastSelectedLocation = (): void => {
  if (!lastSelectedLocations.value?.length) {
    return;
  }

  selectedLocations.value = [ ...lastSelectedLocations.value ];
  suggestedLocations.value = [];

  autocompleteBuffer.value = !allowMultipleSelection.value ? lastSelectedLocations.value[0] : null;
};

const placeholder = computed<string>(() => (selectedLocations.value.length && allowMultipleSelection.value
  ? trans('search.filters.location.placeholder.other_locality')
  : trans('search.filters.location.placeholder.default')));

const showGeolocatingButton = computed<boolean>(() => {
  if (autocompleteBuffer.value && isGeolocated.value) {
    return false;
  }

  return !suggestedLocations.value.length;
});

onMounted(focusInput);

watch(suggestedLocations, () => {
  highlightedIndex.value = 0;
});

watch(autocompleteBuffer, (value) => {
  if (value === null || value === '') {
    isGeolocated.value = false;
  }
});
</script>

<template>
  <div
    :class="[
      'p-3 lg:p-4',
      'flex flex-col gap-y-4',
    ]"
  >
    <div class="relative flex items-center">
      <div
        :class="[
          'absolute left-3 z-10',
          'flex',
        ]"
      >
        <Icon
          v-if="isGeolocated"
          :svg="IconCrosshairFill"
          width="24"
          height="24"
        />

        <Icon
          v-else
          :svg="IconMagnifyingGlass"
          width="24"
          height="24"
        />
      </div>

      <AutoComplete
        ref="autocompleteRef"
        v-model="autocompleteBuffer"
        :suggestions="suggestedLocations"
        :option-label="getOptionLabel"
        :placeholder="placeholder"
        class="w-full"
        input-class="h-14 !pl-10 rounded-lg"
        panel-class="hidden"
        @complete="search"
        @keydown="onKeyDown"
      />

      <Button
        v-if="autocompleteBuffer"
        variant="invisible"
        :class="[
          '!absolute right-2 z-10',
          '!h-10 !w-10',
          '!p-0',
          '!flex items-center justify-center',
          'hover:!bg-transparent hover:!text-black',
        ]"
        @click.stop="clear"
      >
        <Icon
          :svg="IconX"
          width="24"
          height="24"
        />
      </Button>
    </div>

    <Button
      v-if="showGeolocatingButton"
      variant="invisible"
      :class="[
        '!h-12',
        '!flex items-center gap-x-2',
        'group',
      ]"
      :disabled="isGeolocating"
      @click="selfGeolocate"
    >
      <Spinner
        v-if="isGeolocating"
        :size="24"
      />

      <Icon
        v-else
        :svg="IconCrosshairFill"
        width="24"
        height="24"
      />

      <span>
        {{ trans('search.filters.location.self_position') }}
      </span>
    </Button>

    <ul v-if="suggestedLocations.length">
      <li
        v-for="(suggestedLocation, index) in suggestedLocations"
        :key="suggestedLocation.placeId"
        @click.stop="() => selectSuggestion(suggestedLocation)"
        @mouseenter="highlightedIndex = index"
      >
        <div
          :class="[
            'w-full',
            'p-2',
            index === highlightedIndex ? 'bg-gray-light' : 'hover:bg-gray-light',
            'flex items-center gap-x-2',
            'cursor-pointer',
          ]"
        >
          <Typography variant="body">
            {{ suggestedLocation.mainText }}, {{ suggestedLocation.secondaryText }}

            <Typography
              as="span"
              variant="body"
              color="gray-dark"
            >
              • {{ getTypeLabel(suggestedLocation.type) }}
            </Typography>
          </Typography>
        </div>
      </li>
    </ul>

    <template v-if="selectedLocations.length && allowMultipleSelection">
      <Divider class="m-0" color="gray" />

      <ul class="flex flex-wrap gap-2">
        <li
          v-for="selectedLocation in selectedLocations"
          :key="selectedLocation.placeId"
        >
          <div
            :class="[
              'rounded-lg',
              'h-8 px-2',
              'flex items-center gap-x-1',
              'bg-blue-lighter',
              'whitespace-nowrap',
            ]"
          >
            <Typography
              variant="body-bold"
              color="blue"
            >
              {{ formatLocationTitle(selectedLocation) }}
            </Typography>

            <Button
              variant="invisible"
              :class="[
                '!size-4',
                '!p-0',
                '!flex items-center justify-center',
                'hover:!bg-transparent hover:!text-black',
              ]"
              @click.stop="() => removeSuggestion(selectedLocation)"
            >
              <Icon
                :svg="IconX"
                width="16"
                height="16"
              />
            </Button>
          </div>
        </li>
      </ul>
    </template>

    <Button
      v-if="lastSelectedLocations?.length"
      variant="invisible"
      :class="[
        '!p-2',
        '!flex items-center gap-x-2'
      ]"
      @click="selectLastSelectedLocation"
    >
      <Icon
        :svg="IconClockClockwise"
        width="24"
        height="24"
        fill="blue"
      />

      <Typography
        variant="body"
        class="text-left"
      >
        {{ lastSelectedLocationsText }}
      </Typography>
    </Button>

    <Button
      variant="invisible"
      :class="[
        'mt-auto',
        '!p-2',
        '!bg-purple-lighter',
        '!flex items-center gap-x-2',
        'group',
      ]"
    >
      <Icon
        :svg="IconMapPinMagic"
        class="shrink-0 text-purple"
        width="20"
        height="20"
      />

      <Typography
        variant="body-small"
        color="purple"
        class="text-left group-hover:underline"
      >
        {{ trans('search.filters.location_ideal.annotation') }}
      </Typography>

      <Icon
        :svg="IconArrowRight"
        class="shrink-0"
        width="16"
        height="16"
        fill="purple"
      />
    </Button>
  </div>
</template>

<style lang="scss" scoped>
:deep(ul) {
  @apply m-0 px-0 list-none;
}
</style>
